import {
  accountSettingsBillingTab,
  contactSalesPricing,
  trialEndPage,
} from './urls';

// Codes gorilla attaches (extensions.code) when createNotebook,
// duplicateNotebook, or createSandbox refuse because the notebook's
// organization has run out of sandbox credit or storage. See
// services/gorilla/api/resolver/notebook_quota.go.
export const SANDBOX_CREDIT_EXHAUSTED_CODE = 'SANDBOX_CREDIT_EXHAUSTED';
export const STORAGE_QUOTA_EXCEEDED_CODE = 'STORAGE_QUOTA_EXCEEDED';

export type NotebookQuotaKind = 'credit' | 'storage';

/** What the user was trying to do when the refusal arrived; it titles the modal. */
export type NotebookQuotaAction = 'create' | 'launch';

export type NotebookQuotaRefusal = {
  kind: NotebookQuotaKind;
  action: NotebookQuotaAction;
  /** The refused organization; absent when the refusal came back through the sandbox gateway. */
  organizationName?: string;
  /** gorilla's billing account type: free, academic, pro, teams, enterprise, 30_day_trial. */
  accountType?: string;
  /**
   * Whether the viewer can act on billing for that organization (org admin or
   * billing-only role). Members cannot see the billing tab, so the modal asks
   * them to find an admin instead of sending them there. Treated as true when
   * the server did not say.
   */
  viewerCanManageBilling: boolean;
};

type GraphQLErrorLike = {
  extensions?: Record<string, unknown>;
};

function refusalFromGraphQLErrors(
  errors: unknown,
  action: NotebookQuotaAction
): NotebookQuotaRefusal | undefined {
  if (!Array.isArray(errors)) {
    return undefined;
  }
  for (const error of errors as GraphQLErrorLike[]) {
    const extensions = error?.extensions;
    const code = extensions?.code;
    let kind: NotebookQuotaKind | undefined;
    if (code === SANDBOX_CREDIT_EXHAUSTED_CODE) {
      kind = 'credit';
    } else if (code === STORAGE_QUOTA_EXCEEDED_CODE) {
      kind = 'storage';
    }
    if (kind == null) {
      continue;
    }
    const organizationName = extensions?.organizationName;
    const accountType = extensions?.accountType;
    return {
      kind,
      action,
      organizationName:
        typeof organizationName === 'string' ? organizationName : undefined,
      accountType: typeof accountType === 'string' ? accountType : undefined,
      viewerCanManageBilling: extensions?.viewerCanManageBilling !== false,
    };
  }
  return undefined;
}

/**
 * Reads a quota refusal off whatever a mutation handed back: a thrown
 * ApolloError (graphQLErrors, or the body of a non-2xx response under
 * networkError.result.errors), an ExecutionResult whose `errors` carried it
 * alongside a null payload, or one GraphQLError from that list. Anything
 * else is not a quota refusal.
 */
export function notebookQuotaRefusalFromError(
  error: unknown,
  action: NotebookQuotaAction = 'create'
): NotebookQuotaRefusal | undefined {
  const shaped = error as
    | {
        graphQLErrors?: unknown;
        networkError?: {result?: {errors?: unknown}} | null;
        errors?: unknown;
        extensions?: unknown;
      }
    | null
    | undefined;
  return (
    refusalFromGraphQLErrors(shaped?.graphQLErrors, action) ??
    refusalFromGraphQLErrors(shaped?.networkError?.result?.errors, action) ??
    refusalFromGraphQLErrors(shaped?.errors, action) ??
    // A single GraphQLError pulled out of a result's `errors` by the caller.
    (shaped?.extensions != null
      ? refusalFromGraphQLErrors([shaped], action)
      : undefined)
  );
}

export type NotebookQuotaLink = {
  label: string;
  href: string;
  /** Opens in a new tab (marketing site) rather than navigating in-app. */
  external: boolean;
};

/**
 * Everything the modal shows for one refusal. `lead` is the bold sentence,
 * `detail` the instruction after it, `action` the banner's call to action
 * (absent when the viewer cannot act on billing).
 */
export type NotebookQuotaPresentation = {
  title: string;
  lead: string;
  detail: string;
  action?: NotebookQuotaLink;
};

/**
 * Copy and destination by the refused organization's plan. Free, Academic,
 * and trial accounts can purchase (trial accounts on the trial-end page,
 * the others on the organization's billing tab); Enterprise, including an
 * enterprise trial, talks to sales. Pro storage is metered and never refuses;
 * Pro credit can only stop at the safety cap, which also goes to sales.
 * `fallbackOrganizationName` (the selected account) stands in when the
 * refusal did not name its organization.
 */
export function notebookQuotaPresentation(
  refusal: NotebookQuotaRefusal,
  fallbackOrganizationName?: string | null
): NotebookQuotaPresentation {
  const title =
    refusal.action === 'launch'
      ? 'Unable to start notebook'
      : 'Unable to create notebook';
  const lead =
    refusal.kind === 'storage'
      ? "You're out of storage."
      : "You're out of sandbox credits.";

  if (!refusal.viewerCanManageBilling) {
    return {
      title,
      lead,
      detail:
        refusal.kind === 'storage'
          ? 'Ask an organization admin to purchase more, or free up storage.'
          : 'Ask an organization admin to purchase more.',
    };
  }

  const href = purchaseHref(refusal, fallbackOrganizationName);
  if (href == null) {
    return {
      title,
      lead,
      detail: 'Please contact our sales team.',
      action: {
        label: 'Contact sales',
        href: contactSalesPricing(),
        external: true,
      },
    };
  }
  return {
    title,
    lead,
    detail:
      refusal.kind === 'storage' ? 'Purchase more or free up storage.' : '',
    action: {
      label:
        refusal.kind === 'storage' ? 'Purchase storage' : 'Purchase credits',
      href,
      external: false,
    },
  };
}

/**
 * Where a self-serve plan buys more, or undefined when the plan cannot buy
 * its way out (Enterprise, Pro at its cap, or no organization to point at).
 */
function purchaseHref(
  refusal: NotebookQuotaRefusal,
  fallbackOrganizationName?: string | null
): string | undefined {
  switch (refusal.accountType) {
    case '30_day_trial':
      return trialEndPage();
    case 'free':
    case 'academic': {
      const organizationName =
        refusal.organizationName ?? fallbackOrganizationName ?? undefined;
      return organizationName != null && organizationName !== ''
        ? accountSettingsBillingTab(organizationName)
        : undefined;
    }
    default:
      return undefined;
  }
}
